import {
  MenuCategory,
  MenuItem,
  DiningTable,
  Order,
  QRTemplate,
  RestaurantProfile,
  OrderStatus,
  ChangeStatus,
  StaffUser,
  CustomerRecord,
  EventBooking
} from '../types';
import {
  initialCategories,
  initialDiningTables,
  initialQRTemplate,
  initialRestaurantProfile,
  initialSampleOrders,
  initialStaffUsers,
  initialEventBookings
} from '../data/initialData';

const STORAGE_KEYS = {
  CATEGORIES: 'ovenstory_categories_v2',
  TABLES: 'ovenstory_tables_v1',
  ORDERS: 'ovenstory_orders_v1',
  QR_TEMPLATE: 'ovenstory_qr_template_v1',
  RESTAURANT: 'ovenstory_profile_v1',
  STAFF: 'ovenstory_staff_users_v1',
  CUSTOMERS: 'ovenstory_customers_v1',
  BOOKINGS: 'ovenstory_event_bookings_v1'
};

export const apiService = {
  // 1. MENU CRUD
  async getMenu(): Promise<MenuCategory[]> {
    try {
      const res = await fetch('/api/menu');
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && json.data?.categories && json.data.categories.length > 0) {
          localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(json.data.categories));
          return json.data.categories;
        }
      }
    } catch (e) {
      console.warn('Falling back to local storage for menu:', e);
    }

    const local = localStorage.getItem(STORAGE_KEYS.CATEGORIES);
    if (local) {
      try {
        return JSON.parse(local);
      } catch (e) {
        console.error(e);
      }
    }
    localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(initialCategories));
    return initialCategories;
  },

  async saveMenuItem(item: MenuItem): Promise<boolean> {
    try {
      const res = await fetch('/api/menu', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_item', item })
      });
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && json.data?.id) {
          item.id = json.data.id;
        }
      }
    } catch (e) {
      console.warn('Backend save item failed, saving locally:', e);
    }

    const categories = await this.getMenu();
    const updatedCategories = categories.map(cat => {
      let items = cat.items || [];
      const exists = items.some(i => i.id === item.id);
      if (cat.id === item.category_id) {
        items = exists ? items.map(i => (i.id === item.id ? item : i)) : [...items, item];
      } else {
        items = items.filter(i => i.id !== item.id);
      }
      return { ...cat, items };
    });

    localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(updatedCategories));
    return true;
  },

  async deleteMenuItem(itemId: string): Promise<boolean> {
    try {
      await fetch('/api/menu', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete_item', itemId })
      });
    } catch (e) {
      console.warn('Backend delete item failed:', e);
    }

    const categories = await this.getMenu();
    const updatedCategories = categories.map(cat => ({
      ...cat,
      items: (cat.items || []).filter(i => i.id !== itemId)
    }));
    localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(updatedCategories));
    return true;
  },

  async saveCategory(category: MenuCategory): Promise<boolean> {
    try {
      const res = await fetch('/api/menu', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_category', category })
      });
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && json.data?.id) {
          category.id = json.data.id;
        }
      }
    } catch (e) {
      console.warn('Backend save category failed:', e);
    }

    const categories = await this.getMenu();
    const exists = categories.some(c => c.id === category.id);
    const updated = exists
      ? categories.map(c => (c.id === category.id ? { ...category, items: c.items || [] } : c))
      : [...categories, { ...category, items: [] }];

    localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(updated));
    return true;
  },

  async deleteCategory(categoryId: string): Promise<boolean> {
    try {
      await fetch('/api/menu', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete_category', categoryId })
      });
    } catch (e) {
      console.warn('Backend delete category failed:', e);
    }

    const categories = await this.getMenu();
    const updated = categories.filter(c => c.id !== categoryId);
    localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(updated));
    return true;
  },

  async toggleItemAvailability(itemId: string, isAvailable: boolean): Promise<boolean> {
    try {
      await fetch('/api/menu', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'toggle_availability', itemId, isAvailable })
      });
    } catch (e) {
      console.warn('Offline toggle item:', e);
    }

    const categories = await this.getMenu();
    for (const cat of categories) {
      if (cat.items) {
        const item = cat.items.find(i => i.id === itemId);
        if (item) {
          item.is_available = isAvailable;
          break;
        }
      }
    }
    localStorage.setItem(STORAGE_KEYS.CATEGORIES, JSON.stringify(categories));
    return true;
  },

  // 2. TABLES & QR CRUD
  async getTables(): Promise<{ tables: DiningTable[]; template: QRTemplate }> {
    try {
      const res = await fetch('/api/tables');
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && json.data?.tables && json.data.tables.length > 0) {
          localStorage.setItem(STORAGE_KEYS.TABLES, JSON.stringify(json.data.tables));
          if (json.data.template) {
            localStorage.setItem(STORAGE_KEYS.QR_TEMPLATE, JSON.stringify(json.data.template));
          }
          return json.data;
        }
      }
    } catch (e) {
      console.warn('Using local tables storage:', e);
    }

    const localTables = localStorage.getItem(STORAGE_KEYS.TABLES);
    const localTemplate = localStorage.getItem(STORAGE_KEYS.QR_TEMPLATE);

    const tables = localTables ? JSON.parse(localTables) : initialDiningTables;
    const template = localTemplate ? JSON.parse(localTemplate) : initialQRTemplate;

    if (!localTables) localStorage.setItem(STORAGE_KEYS.TABLES, JSON.stringify(initialDiningTables));
    if (!localTemplate) localStorage.setItem(STORAGE_KEYS.QR_TEMPLATE, JSON.stringify(initialQRTemplate));

    return { tables, template };
  },

  async saveTable(table: DiningTable): Promise<boolean> {
    try {
      await fetch('/api/tables', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_table', table })
      });
    } catch (e) {
      console.warn(e);
    }

    const { tables } = await this.getTables();
    const exists = tables.some(t => t.id === table.id || t.table_number === table.table_number);
    const updated = exists
      ? tables.map(t => (t.id === table.id || t.table_number === table.table_number ? table : t))
      : [...tables, table];

    localStorage.setItem(STORAGE_KEYS.TABLES, JSON.stringify(updated));
    return true;
  },

  async saveTables(tables: DiningTable[]): Promise<boolean> {
    try {
      await fetch('/api/tables', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_all_tables', tables })
      });
    } catch (e) {
      console.warn(e);
    }
    localStorage.setItem(STORAGE_KEYS.TABLES, JSON.stringify(tables));
    return true;
  },

  async deleteTable(tableId: string): Promise<boolean> {
    try {
      await fetch('/api/tables', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete_table', tableId })
      });
    } catch (e) {
      console.warn(e);
    }

    const { tables } = await this.getTables();
    const updated = tables.filter(t => t.id !== tableId);
    localStorage.setItem(STORAGE_KEYS.TABLES, JSON.stringify(updated));
    return true;
  },

  async saveQRTemplate(template: QRTemplate): Promise<boolean> {
    try {
      await fetch('/api/tables', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_qr_template', template })
      });
    } catch (e) {
      console.warn(e);
    }
    localStorage.setItem(STORAGE_KEYS.QR_TEMPLATE, JSON.stringify(template));
    return true;
  },

  // 3. ORDERS
  async getOrders(): Promise<Order[]> {
    try {
      const res = await fetch('/api/orders');
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && Array.isArray(json.data)) {
          localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(json.data));
          return json.data;
        }
      }
    } catch (e) {
      console.warn('Using local orders storage:', e);
    }

    const local = localStorage.getItem(STORAGE_KEYS.ORDERS);
    if (local) {
      try {
        return JSON.parse(local);
      } catch (e) {
        console.error(e);
      }
    }
    return initialSampleOrders;
  },

  async getCustomerOrdersByPhone(phone: string): Promise<Order[]> {
    try {
      const cleanPhone = encodeURIComponent(phone.trim());
      const res = await fetch(`/api/orders?phone=${cleanPhone}`);
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && Array.isArray(json.data)) {
          return json.data;
        }
      }
    } catch (e) {
      console.warn('Failed to fetch orders by phone from backend:', e);
    }

    const localOrders = await this.getOrders();
    const clean = phone.trim();
    return localOrders.filter(
      o =>
        (o.customer_phone && o.customer_phone.includes(clean)) ||
        (o.customer_id && o.customer_id.includes(clean))
    );
  },

  async getOrderById(orderId: string): Promise<Order | null> {
    try {
      const res = await fetch(`/api/orders?orderId=${encodeURIComponent(orderId)}`);
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && json.data) {
          return json.data;
        }
      }
    } catch (e) {
      console.warn('Error fetching order by id:', e);
    }

    const orders = await this.getOrders();
    return orders.find(o => o.id === orderId) || null;
  },

  async createOrder(order: Order): Promise<Order> {
    try {
      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create_order', order })
      });
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && json.data) {
          order.id = json.data.id;
          order.order_number = json.data.order_number;
        }
      }
    } catch (e) {
      console.warn('Offline fallback for order creation:', e);
    }

    const currentOrders = await this.getOrders();
    const updated = [order, ...currentOrders.filter(o => o.id !== order.id)];
    localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(updated));
    return order;
  },

  async updateOrderStatus(orderId: string, status: OrderStatus): Promise<boolean> {
    try {
      await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_status', orderId, status })
      });
    } catch (e) {
      console.warn(e);
    }

    const orders = await this.getOrders();
    const order = orders.find(o => o.id === orderId);
    if (order) {
      order.status = status;
      const now = new Date().toISOString();
      if (status === 'confirmed') order.confirmed_at = now;
      if (status === 'preparing') order.preparing_at = now;
      if (status === 'ready') order.ready_at = now;
      if (status === 'completed') order.completed_at = now;
      localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(orders));
      return true;
    }
    return false;
  },

  async requestOrderChange(orderId: string, notes: string): Promise<boolean> {
    try {
      await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'request_change', orderId, changeNotes: notes })
      });
    } catch (e) {
      console.warn(e);
    }

    const orders = await this.getOrders();
    const order = orders.find(o => o.id === orderId);
    if (order) {
      order.change_status = 'requested';
      order.change_request_notes = notes;
      localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(orders));
      return true;
    }
    return false;
  },

  async reviewOrderChange(orderId: string, decision: 'approved' | 'rejected'): Promise<boolean> {
    try {
      await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'review_change', orderId, decision })
      });
    } catch (e) {
      console.warn(e);
    }

    const orders = await this.getOrders();
    const order = orders.find(o => o.id === orderId);
    if (order) {
      order.change_status = decision;
      localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(orders));
      return true;
    }
    return false;
  },

  async updateOrder(order: Order): Promise<boolean> {
    try {
      await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_order', orderId: order.id, order })
      });
    } catch (e) {
      console.warn(e);
    }

    const orders = await this.getOrders();
    const idx = orders.findIndex(o => o.id === order.id);
    if (idx !== -1) {
      orders[idx] = order;
    } else {
      orders.push(order);
    }
    localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(orders));
    return true;
  },

  async cancelOrder(orderId: string): Promise<boolean> {
    return this.updateOrderStatus(orderId, 'cancelled');
  },

  // 4. RESTAURANT PROFILE
  getRestaurantProfile(): RestaurantProfile {
    const local = localStorage.getItem(STORAGE_KEYS.RESTAURANT);
    if (local) {
      try {
        return JSON.parse(local);
      } catch (e) {
        console.error(e);
      }
    }
    localStorage.setItem(STORAGE_KEYS.RESTAURANT, JSON.stringify(initialRestaurantProfile));
    return initialRestaurantProfile;
  },

  saveRestaurantProfile(profile: RestaurantProfile): boolean {
    localStorage.setItem(STORAGE_KEYS.RESTAURANT, JSON.stringify(profile));
    return true;
  },

  // 5. STAFF & AUTH
  async getStaffUsers(): Promise<StaffUser[]> {
    try {
      const res = await fetch('/api/staff');
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && Array.isArray(json.data?.staff)) {
          localStorage.setItem(STORAGE_KEYS.STAFF, JSON.stringify(json.data.staff));
          return json.data.staff;
        }
      }
    } catch (e) {
      console.warn('Falling back to local staff storage:', e);
    }

    const local = localStorage.getItem(STORAGE_KEYS.STAFF);
    if (local) {
      try {
        return JSON.parse(local);
      } catch (e) {
        console.error(e);
      }
    }
    return initialStaffUsers;
  },

  async addStaffUser(user: Omit<StaffUser, 'id'>): Promise<StaffUser> {
    try {
      const res = await fetch('/api/staff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user)
      });
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && json.data?.user) {
          const allStaff = await this.getStaffUsers();
          const updated = [...allStaff.filter(u => u.id !== json.data.user.id), json.data.user];
          localStorage.setItem(STORAGE_KEYS.STAFF, JSON.stringify(updated));
          return json.data.user;
        }
      }
    } catch (e) {
      console.warn('Staff add failed on backend:', e);
    }

    const created: StaffUser = {
      ...user,
      id: `usr_${Date.now()}`
    };
    const all = await this.getStaffUsers();
    localStorage.setItem(STORAGE_KEYS.STAFF, JSON.stringify([...all, created]));
    return created;
  },

  async updateStaffUser(id: string, updates: Partial<StaffUser>): Promise<boolean> {
    try {
      await fetch('/api/staff', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...updates })
      });
    } catch (e) {
      console.warn('Staff update failed on backend:', e);
    }

    const all = await this.getStaffUsers();
    const updated = all.map(u => (u.id === id ? { ...u, ...updates } : u));
    localStorage.setItem(STORAGE_KEYS.STAFF, JSON.stringify(updated));
    return true;
  },

  async deleteStaffUser(id: string): Promise<boolean> {
    try {
      await fetch(`/api/staff?id=${encodeURIComponent(id)}`, {
        method: 'DELETE'
      });
    } catch (e) {
      console.warn('Staff delete failed on backend:', e);
    }

    const all = await this.getStaffUsers();
    const updated = all.filter(u => u.id !== id);
    localStorage.setItem(STORAGE_KEYS.STAFF, JSON.stringify(updated));
    return true;
  },

  async authenticateStaff(credentials: {
    login_type?: 'pin' | 'password';
    pin_code?: string;
    identifier?: string;
    password?: string;
    role?: string;
  }): Promise<{ success: boolean; user?: StaffUser; error?: string }> {
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && json.data?.user) {
          return { success: true, user: json.data.user };
        } else {
          return { success: false, error: json.error || 'Authentication failed' };
        }
      }
    } catch (e) {
      console.warn('Auth backend error:', e);
    }

    const localUsers = await this.getStaffUsers();
    if (credentials.login_type === 'pin' && credentials.pin_code) {
      const match = localUsers.find(u => u.pin_code === credentials.pin_code && u.is_active);
      if (match) return { success: true, user: match };
    } else if (credentials.identifier && credentials.password) {
      const match = localUsers.find(
        u =>
          (u.email?.toLowerCase() === credentials.identifier?.toLowerCase() ||
            u.mobile === credentials.identifier) &&
          u.password === credentials.password &&
          u.is_active
      );
      if (match) return { success: true, user: match };
    }

    return { success: false, error: 'Invalid PIN or credentials' };
  },

  async getSettings(): Promise<any> {
    try {
      const res = await fetch('/api/settings');
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && json.data) {
          return json.data;
        }
      }
    } catch (e) {
      console.warn('Error fetching settings:', e);
    }
    return null;
  },

  async saveSettings(settings: any): Promise<boolean> {
    try {
      await fetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      return true;
    } catch (e) {
      console.warn('Error saving settings:', e);
      return false;
    }
  },

  // 6. CUSTOMERS & CRM CRUD
  async getCustomers(): Promise<CustomerRecord[]> {
    try {
      const res = await fetch('/api/customers');
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && Array.isArray(json.data)) {
          localStorage.setItem(STORAGE_KEYS.CUSTOMERS, JSON.stringify(json.data));
          return json.data;
        }
      }
    } catch (e) {
      console.warn('Error fetching customers:', e);
    }

    const local = localStorage.getItem(STORAGE_KEYS.CUSTOMERS);
    if (local) {
      try {
        return JSON.parse(local);
      } catch (e) {
        console.error(e);
      }
    }
    return [];
  },

  async createCustomer(customer: {
    name: string;
    phone: string;
    whatsapp_number?: string;
    notes?: string;
  }): Promise<{ success: boolean; data?: CustomerRecord; error?: string }> {
    try {
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create_customer', ...customer })
      });
      const json: any = await res.json();
      if (res.ok && json.success) {
        const all = await this.getCustomers();
        const updated = [json.data, ...all.filter(c => c.id !== json.data.id)];
        localStorage.setItem(STORAGE_KEYS.CUSTOMERS, JSON.stringify(updated));
        return { success: true, data: json.data };
      } else {
        return { success: false, error: json.error || 'Failed to create customer' };
      }
    } catch (e: any) {
      const all = await this.getCustomers();
      const existing = all.find(c => c.phone === customer.phone);
      if (existing) {
        return { success: false, error: `Customer with phone ${customer.phone} already exists.` };
      }
      const localCustomer: CustomerRecord = {
        id: customer.phone,
        name: customer.name,
        phone: customer.phone,
        whatsapp_number: customer.whatsapp_number || customer.phone,
        visit_count: 1,
        total_spent: 0,
        notes: customer.notes || '',
        last_visit: new Date().toISOString(),
        created_at: new Date().toISOString(),
        orders: []
      };
      localStorage.setItem(STORAGE_KEYS.CUSTOMERS, JSON.stringify([localCustomer, ...all]));
      return { success: true, data: localCustomer };
    }
  },

  async updateCustomer(customerId: string, updates: Partial<CustomerRecord>): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_customer', customerId, ...updates })
      });
      const json: any = await res.json();
      if (!res.ok || !json.success) {
        return { success: false, error: json.error || 'Failed to update customer' };
      }
    } catch (e: any) {
      console.warn('Backend update failed:', e);
    }

    const all = await this.getCustomers();
    const updated = all.map(c => (c.id === customerId ? { ...c, ...updates } : c));
    localStorage.setItem(STORAGE_KEYS.CUSTOMERS, JSON.stringify(updated));
    return { success: true };
  },

  async deleteCustomer(customerId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete_customer', customerId })
      });
      const json: any = await res.json();
      if (!res.ok || !json.success) {
        return { success: false, error: json.error || 'Failed to delete customer' };
      }
    } catch (e: any) {
      console.warn('Backend delete failed:', e);
    }

    const all = await this.getCustomers();
    const updated = all.filter(c => c.id !== customerId);
    localStorage.setItem(STORAGE_KEYS.CUSTOMERS, JSON.stringify(updated));
    return { success: true };
  },

  async updateCustomerNotes(customerId: string, notes: string): Promise<boolean> {
    try {
      await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_notes', customerId, notes })
      });
      return true;
    } catch (e) {
      console.warn('Error updating customer notes:', e);
      return false;
    }
  },

  // 10. EVENT & PRIVATE BOOKINGS
  async getEventBookings(): Promise<EventBooking[]> {
    try {
      const res = await fetch('/api/bookings');
      if (res.ok) {
        const json: any = await res.json();
        if (json.success && Array.isArray(json.data) && json.data.length > 0) {
          localStorage.setItem(STORAGE_KEYS.BOOKINGS, JSON.stringify(json.data));
          return json.data;
        }
      }
    } catch (e) {
      console.warn('Falling back to local storage for bookings:', e);
    }

    const local = localStorage.getItem(STORAGE_KEYS.BOOKINGS);
    if (local) {
      try {
        return JSON.parse(local);
      } catch (e) {
        console.error(e);
      }
    }
    localStorage.setItem(STORAGE_KEYS.BOOKINGS, JSON.stringify(initialEventBookings));
    return initialEventBookings;
  },

  async saveEventBooking(booking: EventBooking): Promise<boolean> {
    try {
      await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', booking })
      });
    } catch (e) {
      console.warn('Backend save booking failed, saving locally:', e);
    }

    const all = await this.getEventBookings();
    const existingIndex = all.findIndex(b => b.id === booking.id);
    if (existingIndex >= 0) {
      all[existingIndex] = booking;
    } else {
      all.unshift(booking);
    }
    localStorage.setItem(STORAGE_KEYS.BOOKINGS, JSON.stringify(all));
    return true;
  },

  async updateEventBookingStatus(id: string, status: EventBooking['status'], staffNotes?: string): Promise<boolean> {
    try {
      await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update_status', id, status, staffNotes })
      });
    } catch (e) {
      console.warn('Backend update booking status failed:', e);
    }

    const all = await this.getEventBookings();
    const updated = all.map(b => b.id === id ? { ...b, status, staffNotes: staffNotes !== undefined ? staffNotes : b.staffNotes } : b);
    localStorage.setItem(STORAGE_KEYS.BOOKINGS, JSON.stringify(updated));
    return true;
  },

  async deleteEventBooking(id: string): Promise<boolean> {
    try {
      await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', id })
      });
    } catch (e) {
      console.warn('Backend delete booking failed:', e);
    }

    const all = await this.getEventBookings();
    const updated = all.filter(b => b.id !== id);
    localStorage.setItem(STORAGE_KEYS.BOOKINGS, JSON.stringify(updated));
    return true;
  }
};
